import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CalendarPeriod } from '@assetaccountant/api/models/calendar-period';
import { PrecalcAvailability } from '@assetaccountant/api/models/precalc-availability';
import { CalendarV2Api } from '@assetaccountant/api/services/calendar-v2.api';
import { AppNavigationService, Register, toUniversalDateString } from '@assetaccountant/core';
import { setupModal, TypedNgbModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { filter } from 'rxjs/operators';
import { SelectPeriodModalComponent } from './select-period-modal/select-period-modal.component';

export type LeanCalendarPeriod = Omit<
  CalendarPeriod,
  'periodNumber' | 'financialYearNumber' | 'days'
>;

@Component({
  selector: 'app-calendar-selector',
  templateUrl: './calendar-selector.component.html',
  styleUrls: ['calendar-selector.component.scss'],
  standalone: false,
})
export class CalendarSelectorComponent implements OnChanges, OnDestroy {
  @Input() selectedRegister: Register;

  @Output() onCalendarChanged = new EventEmitter<{ startDate: string; endDate: string }>();

  selectedPeriodEndDate: string;
  selectedPeriodStartDate: string;

  periodNavGroups: Array<{ items: CalendarPeriodNavItem[]; name?: string }> = [];
  calendarPeriods: LeanCalendarPeriod[] = [];

  private subscriptions = [
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd && /\bperiods\b/.test(e.url)))
      .subscribe(() => this.updateCalendars()),
  ];

  constructor(
    private appNavigationService: AppNavigationService,
    private calendarApi: CalendarV2Api,
    @Inject(LOCALE_ID) public locale: string,
    private router: Router,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
  ) {}

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ('selectedRegister' in changes) {
      await this.updateCalendars();
    }
  }

  onUserChangedCalendarPeriod(calendarPeriodStartDate: string, calendarPeriodEndDate: string) {
    this.selectCalendarPeriod(calendarPeriodStartDate, calendarPeriodEndDate);
  }

  openSelectPeriodModal() {
    setupModal(
      this.ngbModal.open(SelectPeriodModalComponent),
      modal => {
        modal.calendarPeriods = this.calendarPeriods;
        modal.currentStartDate = this.selectedPeriodStartDate;
        modal.currentEndDate = this.selectedPeriodEndDate;
      },
      modal =>
        modal.selectPeriod.subscribe(periods =>
          this.onUserChangedCalendarPeriod(periods.startDate, periods.endDate),
        ),
    );
  }

  async selectCalendarPeriod(
    selectedPeriodStartDate: string,
    selectedPeriodEndDate: string,
    fireEvent = true,
  ) {
    this.selectedPeriodEndDate = selectedPeriodEndDate;
    this.selectedPeriodStartDate = selectedPeriodStartDate;

    await this.appNavigationService.setPeriod(selectedPeriodStartDate, selectedPeriodEndDate);

    const todayIndex = this.getTodayIndex();
    const currentPeriod = this.calendarPeriods[todayIndex];
    const selectedIndex = this.getSelectedIndex();
    const selectedPeriod = selectedIndex >= 0 ? this.calendarPeriods[selectedIndex] : undefined;

    if (!currentPeriod) {
      throw new Error('No period found for today');
    }

    const isSingleOrYtdOrQuarterSelected =
      selectedPeriod &&
      ((this.selectedPeriodEndDate === selectedPeriod?.endDate &&
        (this.selectedPeriodStartDate === selectedPeriod?.startDate ||
          this.selectedPeriodStartDate === getFYStart(selectedPeriod))) ||
        (this.selectedPeriodStartDate === getQuarterStart(this.calendarPeriods) &&
          this.selectedPeriodEndDate === getQuarterEnd(this.calendarPeriods)));

    const currentYearPeriods = this.calendarPeriods.filter(
      cp => cp.endOfPreviousFinancialYearDate === currentPeriod.endOfPreviousFinancialYearDate,
    );

    const noPeriods = currentYearPeriods.length === 13 ? 13 : 12;

    this.periodNavGroups = [
      {
        name: 'Periods',
        items: [
          {
            name: 'Current Period',
            actionSlug: 'current-period',
            ...currentPeriod,
            isSelectionAvailable: true,
            gridIcon: this.getGridIconArray(noPeriods, 8, 8),
          },
        ],
      },
    ];

    if (currentYearPeriods.length === 12) {
      const currentQuarterIndex = Math.floor(currentYearPeriods.indexOf(currentPeriod) / 3);
      this.periodNavGroups.push({
        name: 'Quarters',
        items: [
          {
            name: 'Current Quarter',
            actionSlug: 'current-quarter',
            startDate: currentYearPeriods[currentQuarterIndex * 3].startDate,
            endDate: currentYearPeriods[currentQuarterIndex * 3 + 2].endDate,
            isSelectionAvailable: true,
            gridIcon: this.getGridIconArray(noPeriods, 7, 9),
          },
        ],
      });

      if (selectedPeriod && isSingleOrYtdOrQuarterSelected) {
        const quarterStartIndex = getQuarterStartIndex(this.calendarPeriods);

        this.periodNavGroups[1].items.push(
          {
            name: 'Previous Quarter',
            actionSlug: 'previous-quarter',
            startDate: this.calendarPeriods[quarterStartIndex - 3].startDate,
            endDate: this.calendarPeriods[quarterStartIndex - 1].endDate,
            isSelectionAvailable: true,
            icon: 'arrow-left',
          },
          {
            name: 'Next Quarter',
            actionSlug: 'next-quarter',
            startDate: this.calendarPeriods[quarterStartIndex + 3].startDate,
            endDate: this.calendarPeriods[quarterStartIndex + 5].endDate,
            isSelectionAvailable: true,
            icon: 'arrow-right',
          },
        );
      }
    }

    const startOfCurrentYear = getFYStart(currentPeriod);
    this.periodNavGroups.push({
      name: 'Year To Date',
      items: [
        {
          name: 'Current YTD',
          actionSlug: 'current-ytd',
          startDate: startOfCurrentYear,
          endDate: currentPeriod.endDate,
          isSelectionAvailable: true,
          gridIcon: this.getGridIconArray(noPeriods, 1, 8),
        },
      ],
    });

    if (selectedPeriod && isSingleOrYtdOrQuarterSelected) {
      const prevPeriod = this.calendarPeriods[selectedIndex - 1];
      const nextPeriod = this.calendarPeriods[selectedIndex + 1];

      this.periodNavGroups[0].items.push(
        {
          name: 'Previous Period',
          actionSlug: 'previous-period',
          ...prevPeriod,
          isSelectionAvailable: true,
          icon: 'arrow-left',
        },
        {
          name: 'Next Period',
          actionSlug: 'next-period',
          ...nextPeriod,
          isSelectionAvailable: true,
          icon: 'arrow-right',
        },
      );

      const prevFYStart = getFYStart(prevPeriod);
      const nextFYStart = getFYStart(nextPeriod);
      this.periodNavGroups[this.periodNavGroups.length - 1].items.push(
        {
          name: 'Previous YTD',
          actionSlug: 'previous-ytd',
          ...prevPeriod,
          startDate: prevFYStart || '',
          isSelectionAvailable: !!(prevPeriod && prevFYStart),
          icon: 'arrow-left',
        },
        {
          name: 'Next YTD',
          actionSlug: 'next-ytd',
          ...nextPeriod,
          startDate: nextFYStart || '',
          isSelectionAvailable: !!(nextPeriod && nextFYStart),
          icon: 'arrow-right',
        },
      );
    }

    const currentFinancialYear = this.getCurrentFinancialYearCalendarPeriod(currentPeriod);
    const previousFinancialYear = this.getPreviousFinancialYear(selectedIndex);
    const nextFinancialYear = this.getNextFinancialYear(selectedIndex);

    this.periodNavGroups.push({
      name: 'Financial Years',
      items: [
        {
          name: 'Current FY',
          actionSlug: 'current-fy',
          ...currentFinancialYear,
          gridIcon: this.getGridIconArray(noPeriods, 1, noPeriods),
        },
        {
          name: 'Previous FY',
          actionSlug: 'previous-fy',
          ...previousFinancialYear,
          icon: 'arrow-left',
        },
        {
          name: 'Next FY',
          actionSlug: 'next-fy',
          ...nextFinancialYear,
          icon: 'arrow-right',
        },
      ],
    });

    // Fetch early so we have a buffer
    if (
      selectedIndex > -1 &&
      (selectedIndex < 24 || selectedIndex + 24 > this.calendarPeriods.length)
    ) {
      await this.updateCalendars();
      if (this.selectedPeriodEndDate && this.selectedPeriodStartDate) {
        await this.selectCalendarPeriod(this.selectedPeriodStartDate, this.selectedPeriodEndDate);
      }
    }

    if (fireEvent) {
      this.onCalendarChanged.emit({
        startDate: toUniversalDateString(selectedPeriodStartDate) || '',
        endDate: toUniversalDateString(selectedPeriodEndDate) || '',
      });
    }

    function getFYStart(period: LeanCalendarPeriod): string;
    function getFYStart(period: LeanCalendarPeriod | undefined): string | null;
    function getFYStart(period: LeanCalendarPeriod | undefined) {
      return period
        ? toUniversalDateString(moment.utc(period.endOfPreviousFinancialYearDate).add(1, 'day'))
        : null;
    }

    function getQuarterStartIndex(calendarPeriods: LeanCalendarPeriod[]) {
      const periodInYearIndex = calendarPeriods.filter(
        cp =>
          cp.endOfPreviousFinancialYearDate ===
            calendarPeriods[selectedIndex].endOfPreviousFinancialYearDate &&
          cp.endDate < calendarPeriods[selectedIndex].endDate,
      ).length;
      const periodInQuarterIndex = periodInYearIndex % 3;
      return selectedIndex - periodInQuarterIndex;
    }
    function getQuarterStart(calendarPeriods: LeanCalendarPeriod[]) {
      return calendarPeriods[getQuarterStartIndex(calendarPeriods)].startDate;
    }

    function getQuarterEndIndex(calendarPeriods: LeanCalendarPeriod[]) {
      return getQuarterStartIndex(calendarPeriods) + 2;
    }
    function getQuarterEnd(calendarPeriods: LeanCalendarPeriod[]) {
      return calendarPeriods[getQuarterEndIndex(calendarPeriods)].endDate;
    }
  }

  private getCurrentFinancialYearCalendarPeriod(currentPeriod: LeanCalendarPeriod): {
    startDate: string;
    endDate: string;
    isSelectionAvailable: boolean;
  } {
    const endOfCurrentFinancialYearDate = this.calendarPeriods.find(
      cp => cp.endOfPreviousFinancialYearDate > currentPeriod.endOfPreviousFinancialYearDate,
    )?.endOfPreviousFinancialYearDate;

    if (endOfCurrentFinancialYearDate) {
      return {
        startDate: toUniversalDateString(
          moment.utc(currentPeriod.endOfPreviousFinancialYearDate).add(1, 'day'),
          { throwOnFailure: true },
        ),
        endDate: toUniversalDateString(endOfCurrentFinancialYearDate, { throwOnFailure: true }),
        isSelectionAvailable: true,
      };
    }

    return {
      startDate: '',
      endDate: '',
      isSelectionAvailable: false,
    };
  }

  private getNextFinancialYear(selectedIndex: number): {
    startDate: string;
    endDate: string;
    isSelectionAvailable: boolean;
  } {
    const nextFYPeriodStart = this.calendarPeriods.find(
      cp =>
        cp.endOfPreviousFinancialYearDate >
        this.calendarPeriods[selectedIndex].endOfPreviousFinancialYearDate,
    );
    const followingFyPeriodStartIndex =
      nextFYPeriodStart &&
      this.calendarPeriods.findIndex(
        cp => cp.endOfPreviousFinancialYearDate > nextFYPeriodStart.endOfPreviousFinancialYearDate,
      );

    if (nextFYPeriodStart && followingFyPeriodStartIndex && followingFyPeriodStartIndex > 0) {
      return {
        startDate: nextFYPeriodStart.startDate,
        endDate: this.calendarPeriods[followingFyPeriodStartIndex - 1].endDate,
        isSelectionAvailable: true,
      };
    }

    return {
      startDate: '',
      endDate: '',
      isSelectionAvailable: false,
    };
  }

  private getPreviousFinancialYear(selectedIndex: number): {
    startDate: string;
    endDate: string;
    isSelectionAvailable: boolean;
  } {
    const previousFYPeriodEnd = this.calendarPeriods.find(
      cp =>
        toUniversalDateString(cp.endDate) ===
        toUniversalDateString(this.calendarPeriods[selectedIndex].endOfPreviousFinancialYearDate),
    );
    const previousFYPeriodStart =
      previousFYPeriodEnd &&
      this.calendarPeriods.find(
        cp =>
          cp.endOfPreviousFinancialYearDate === previousFYPeriodEnd.endOfPreviousFinancialYearDate,
      );

    if (previousFYPeriodStart && previousFYPeriodEnd) {
      return {
        startDate: previousFYPeriodStart.startDate,
        endDate: previousFYPeriodEnd.endDate,
        isSelectionAvailable: true,
      };
    }

    return {
      startDate: '',
      endDate: '',
      isSelectionAvailable: false,
    };
  }

  private getTodayIndex() {
    const today = toUniversalDateString(moment().toISOString());

    if (!today) {
      throw new Error('today should be valid date string');
    }

    return this.calendarPeriods.findIndex(cp => {
      const startDate = toUniversalDateString(cp.startDate);
      const endDate = toUniversalDateString(cp.endDate);
      return !!startDate && !!endDate && startDate <= today && today <= endDate;
    });
  }

  private setDefaultCalendarPeriod() {
    if (this.calendarPeriods && this.calendarPeriods.length > 0) {
      const currentPeriodStartDate = this.appNavigationService.getPeriodStartDate() || '';
      let currentPeriodEndDate = this.appNavigationService.getPeriodEndDate();

      if (!currentPeriodEndDate) {
        const today = moment().toISOString();

        const calendarPeriod =
          this.calendarPeriods.find(cp => cp.endDate > today) || this.calendarPeriods[0];

        currentPeriodEndDate = calendarPeriod.endDate;
      }

      this.selectCalendarPeriod(currentPeriodStartDate, currentPeriodEndDate, false);
    }
  }

  private async updateCalendars() {
    const regimeId = this.appNavigationService.getActiveRegimeId();
    // The URL regime is old during a register switch. The redirect's NavigationEnd reloads it.
    if (regimeId && !this.selectedRegister.regimes?.some(r => r.id === regimeId)) {
      return;
    }

    const currentPeriodStartDate = this.appNavigationService.getPeriodStartDate();
    const periodsResponse = await this.calendarApi
      .getPeriods({
        registerId: this.selectedRegister.id,
        regimeId,
        startDate:
          currentPeriodStartDate &&
          toUniversalDateString(
            moment.min(moment.utc(currentPeriodStartDate), moment.utc()).add(-5, 'years'),
            {
              throwOnFailure: true,
            },
          ),
      })
      .toPromise();

    const periods = periodsResponse?.periods || [];

    this.calendarPeriods = periods.map(p => ({
      startDate: p.startDate,
      endDate: p.endDate,
      endOfPreviousFinancialYearDate: toUniversalDateString(
        moment.utc(p.fyStartDate).add(-1, 'day'),
        { throwOnFailure: true },
      ),
      precalcAvailable: PrecalcAvailability.Month,
    }));
    this.setDefaultCalendarPeriod();
  }

  private getGridIconArray(noPeriods: number, start: number, end: number): boolean[] {
    const icon: boolean[] = [];
    for (let index = 0; index < noPeriods; index++) {
      icon.push(index >= start - 1 && index <= end - 1);
    }
    return icon;
  }

  private getSelectedIndex(): number {
    return this.calendarPeriods.findIndex(
      cp =>
        !!this.selectedPeriodEndDate &&
        cp.startDate <= this.selectedPeriodEndDate &&
        this.selectedPeriodEndDate <= cp.endDate,
    );
  }
}

interface CalendarPeriodNavItem extends Partial<LeanCalendarPeriod> {
  name: string;
  actionSlug: string;
  startDate: string;
  endDate: string;
  isSelectionAvailable: boolean;
  icon?: string;
  gridIcon?: boolean[];
}
